import axios from 'axios';

const client = axios.create({
  baseURL: '/api/consumer/v1',
  withCredentials: true,
});

export { client };

let _refreshing = null;

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/refresh/')
    ) {
      original._retry = true;
      try {
        if (!_refreshing) {
          _refreshing = client.post('/auth/refresh/').finally(() => {
            _refreshing = null;
          });
        }
        await _refreshing;
        return client(original);
      } catch {
        const { useAuth } = await import('@/store/useAuth');
        useAuth.getState().forceLogout();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const login = (email, password) =>
  client.post('/auth/login/', { email, password }).then((r) => r.data);

export const register = (payload) =>
  client.post('/auth/register/', payload).then((r) => r.data);

export const socialLoginGoogle = ({ id_token, access_token } = {}) =>
  client.post('/auth/social/google/', { id_token, access_token }).then((r) => r.data);

export const socialLoginFacebook = (access_token) =>
  client.post('/auth/social/facebook/', { access_token }).then((r) => r.data);

export const socialLoginApple = (identity_token, user) =>
  client.post('/auth/social/apple/', { identity_token, user }).then((r) => r.data);

export const logout = () =>
  client.post('/auth/logout/').then((r) => r.data);

export const refresh = () =>
  client.post('/auth/refresh/').then((r) => r.data);

export const getMe = () =>
  client.get('/me/').then((r) => r.data);

export const updateProfile = (patch) =>
  client.patch('/me/profile/', patch).then((r) => r.data);

export const verifyEmail = (token) =>
  client.post('/auth/verify-email/', { token }).then((r) => r.data);

export const resendVerification = (email) =>
  client.post('/auth/resend-verification/', email ? { email } : {}).then((r) => r.data);

export const passwordResetRequest = (email) =>
  client.post('/auth/password-reset/request/', { email }).then((r) => r.data);

export const passwordResetConfirm = (token, password) =>
  client.post('/auth/password-reset/confirm/', { token, password }).then((r) => r.data);

export const getSubscription = () =>
  client.get('/me/subscription/').then((r) => r.data);

export const createCheckoutSession = (package_id, billing_cycle) =>
  client.post('/me/checkout-session/', { package_id, billing_cycle }).then((r) => r.data);

export const createBillingPortal = () =>
  client.post('/me/billing-portal/').then((r) => r.data);

export const getPackages = () =>
  client.get('/packages/', { baseURL: '/api/consumer/v1' }).then((r) => r.data);

export const listSavedSearches = () =>
  client.get('/me/saved-searches/').then((r) => r.data);

export const createSavedSearch = (name, filters) =>
  client.post('/me/saved-searches/', { name, filters }).then((r) => r.data);

export const deleteSavedSearch = (id) =>
  client.delete(`/me/saved-searches/${id}/`).then((r) => r.data);

export const listNotifications = () =>
  client.get('/me/notifications/').then((r) => r.data);

export const markAllNotificationsRead = () =>
  client.post('/me/notifications/all/read/').then((r) => r.data);

export const markNotificationRead = (id) =>
  client.post(`/me/notifications/${id}/read/`).then((r) => r.data);

export const getNotificationPreferences = () =>
  client.get('/me/notification-preferences/').then((r) => r.data);

export const updateNotificationPreferences = (prefs) =>
  client.put('/me/notification-preferences/', prefs).then((r) => r.data);

export const listAlerts = () =>
  client.get('/me/alerts/').then((r) => r.data);

export const createAlert = (payload) =>
  client.post('/me/alerts/', payload).then((r) => r.data);

export const updateAlert = (id, payload) =>
  client.patch(`/me/alerts/${id}/`, payload).then((r) => r.data);

export const deleteAlert = (id) =>
  client.delete(`/me/alerts/${id}/`).then((r) => r.data);
